import React, { useEffect, useRef, useMemo } from "react";
import useAbandonedCartIntent from "@/hooks/useAbandonedCartIntent";
import { formatCurrency, CartItem } from "@/utils/cartUtils";
import CTALink from "@/components/CTA/ctaLink";
import { ElementType } from "@/constants/tagular/main";
import makeEvent from "@/hooks/eventing/makeEvent";
import { EVENTS } from "@/hooks/eventing/types/events";
import { getCorrelationID } from "@/utils/tagular/helpers";
import {
  createCartIntentProductViewEventData,
  createCartIntentConversionEventData,
} from "./utils/eventHelpers";

interface CartSuggestionsProps {
  ctaText?: string;
}

export default function CartSuggestions({ ctaText }: CartSuggestionsProps) {
  const { suggestedProducts, isLoading, error } = useAbandonedCartIntent(1);

  // Generate correlationId once using lazy ref initialization (React-safe)
  const correlationIdRef = useRef<string | undefined>(undefined);
  if (correlationIdRef.current === undefined) {
    correlationIdRef.current = getCorrelationID();
  }

  // Memoize validProducts to prevent unnecessary re-renders and duplicate events
  const validProducts = useMemo(
    () =>
      suggestedProducts.filter(
        (product) =>
          product.productName &&
          product.productUrl &&
          product.checkoutUrl &&
          product.metadata?.price !== undefined,
      ),
    [suggestedProducts],
  );

  // Track Product Viewed and Conversion Tracked when products are shown
  useEffect(() => {
    if (validProducts.length > 0) {
      const correlationId = correlationIdRef.current!;

      // Create CartItem array once for reuse
      const cartItems: CartItem[] = validProducts.map((product) => ({
        id: product.productId,
        courseName: product.productName,
        price: product.metadata?.price ?? 0,
        originalPrice: product.metadata?.price ?? 0,
        totalPrice: product.metadata?.discountedPrice,
        quantity: 1,
        type: "course",
        courseKey: product.productId,
        courseRunKey: product.productId,
        brand: "edX",
      }));

      // Fire Product Viewed event for each cart item
      cartItems.forEach((cartItem, index) => {
        const productViewedEvent = makeEvent(
          createCartIntentProductViewEventData(
            cartItem,
            index,
            "edX",
            "nav-bar",
            "cart",
            correlationId,
          ),
        );
        productViewedEvent(EVENTS.ProductViewed)();
      });
    }
  }, [validProducts]);

  if (isLoading || error || validProducts.length === 0) {
    return null;
  }

  return (
    <div className="min-w-96 bg-putty-200 px-3.5 py-4">
      <p className="text-[16px] font-bold italic text-brand m-0">
        Are you still interested in
      </p>

      <div className="flex flex-col gap-2 mt-2">
        {validProducts.map((product) => (
          <div key={product.productId} className="flex flex-col gap-4">
            <a
              href={product.productUrl}
              className="text-[16px] font-bold underline text-gray-800"
            >
              {product.productName}
            </a>

            <div className="flex items-center justify-end gap-4">
              <p className="text-base text-gray-800 font-bold m-0">
                {product.metadata?.discountedPrice ? (
                  <>
                    <span className="line-through text-gray-500">
                      {formatCurrency(product.metadata?.price ?? 0)}
                    </span>{" "}
                    {formatCurrency(product.metadata?.discountedPrice ?? 0)}
                  </>
                ) : (
                  formatCurrency(product.metadata?.price ?? 0)
                )}
              </p>

              <CTALink
                label={ctaText ?? "Add to cart"}
                url={product.checkoutUrl}
                type="brand"
                element_type={ElementType.Link}
                html_id="add-to-cart"
                location="abandoned-cart-modal"
                name="add-to-cart"
                enable_tracking={true}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
